/**
 * MODULE: domain/sales/frontend/components/accounts/StakeholderRiskBadges.tsx
 * PURPOSE: Compact risk badges for an org-chart contact node — buying role, relationship strength,
 *          sentiment, and a "gone quiet" alert. Shared by InteractiveOrgChart + OrgChartNode.
 * PUBLIC API: StakeholderRiskBadges
 * SIDE EFFECTS: None
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~95 / 200 lines
 * RECENT: 2026-07-07 [2.2.1521] — New: risk-aware stakeholder badges.
 */

import { Tooltip } from 'flowbite-react';
import { HiOutlineExclamation } from 'react-icons/hi';
import type { Contact } from '../../../contracts/sales.types';
import {
  RELATIONSHIP_STRENGTH_CONFIG,
  SENTIMENT_CONFIG,
  getStakeholderRisk,
} from './stakeholderRisk';

export interface StakeholderRiskBadgesProps {
  contact: Pick<Contact, 'buying_role' | 'relationship_strength' | 'sentiment' | 'last_engaged_at'>;
  /** Compact hides the text label on the role pill (kanban-dense contexts). */
  compact?: boolean;
}

export function StakeholderRiskBadges({ contact, compact = false }: StakeholderRiskBadgesProps) {
  const risk = getStakeholderRisk(contact);
  if (!risk.hasAny && !risk.quiet) return null;

  const strength = risk.strength ? RELATIONSHIP_STRENGTH_CONFIG[risk.strength] : null;
  const sentiment = risk.sentiment ? SENTIMENT_CONFIG[risk.sentiment] : null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="stakeholder-risk-badges">
      {risk.roleConfig && (
        <span
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${risk.roleConfig.badgeClass}`}
          data-testid="stakeholder-role-badge"
        >
          {compact ? risk.roleConfig.short : risk.roleConfig.label}
        </span>
      )}

      {strength && (
        <Tooltip content={strength.label}>
          <span className="inline-flex items-center gap-0.5" aria-label={strength.label}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${
                  i < strength.dots ? strength.dotClass : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </span>
        </Tooltip>
      )}

      {sentiment && (
        <Tooltip content={sentiment.label}>
          <span className={`text-xs font-bold leading-none ${sentiment.className}`} aria-label={sentiment.label}>
            {sentiment.symbol}
          </span>
        </Tooltip>
      )}

      {risk.quiet && (
        <Tooltip
          content={`Gone quiet — no engagement in ${risk.quietDays} days. Re-engage this key stakeholder.`}
        >
          <span
            className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
            data-testid="stakeholder-quiet-badge"
          >
            <HiOutlineExclamation className="h-3 w-3" />
            Gone quiet
          </span>
        </Tooltip>
      )}
    </div>
  );
}

export default StakeholderRiskBadges;
